import { palette } from "lib/miolo";
import formatFinancialValue from "components/company/financials/formatFinancialValue";
import FinancialValueThresholdColumnStyles from "components/financials/FinancialValueThresholdColumnStyles";
import TextTooltip from "components/shared/TextTooltip";
import { getYear } from "date-fns";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import filterUnionByTypename from "lib/utils/filterUnionByTypename";
import parseDateYmd from "lib/utils/parseDateYmd";
import numeral from "numeral";

interface ITotalCompensationsProps {
  totalCompensations: IBrazilianCompanyIssuerTopLevelFragment["totalCompensations"];
  financials: IBrazilianCompanyIssuerTopLevelFragment["financials"];
}

export default function TotalCompensations({ totalCompensations, financials }: ITotalCompensationsProps) {
  const { yearly } = financials;

  const yearlyBankFinancials = filterUnionByTypename(yearly, "YearlyBrazilianBankFinancialStatementRow");
  const yearlyCompanyFinancials = filterUnionByTypename(yearly, "YearlyBrazilianCompanyFinancialStatementRow");
  const yearlyInsurerFinancials = filterUnionByTypename(yearly, "YearlyBrazilianInsurerFinancialStatementRow");

  const isBank = yearlyBankFinancials.length > 0;
  const isCompany = yearlyCompanyFinancials.length > 0;
  const isInsurer = yearlyInsurerFinancials.length > 0;

  const actualCompensations = totalCompensations
    .reduce<
      {
        fiscalYear: number;

        boardOfDirectors: number;
        boardOfExecutiveOfficers: number;
        fiscalCouncil: number;
        totalCompensation: number;

        netRevenue: number;
        netIncome: number;
      }[]
    >((accumulator, compensation) => {
      if (compensation.totalCompensation === 0) {
        return accumulator;
      }

      const compensationFiscalYearEnd = getYear(parseDateYmd(compensation.fiscalYearEnd));

      if (isBank) {
        const financialStatementRow = yearlyBankFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.financialIntermediationRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      if (isCompany) {
        const financialStatementRow = yearlyCompanyFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.netRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      if (isInsurer) {
        const financialStatementRow = yearlyInsurerFinancials.find(
          ({ endOfPeriod }) => getYear(parseDateYmd(endOfPeriod)) === compensationFiscalYearEnd,
        );

        if (financialStatementRow) {
          return accumulator.concat({
            ...compensation,
            fiscalYear: getYear(parseDateYmd(compensation.fiscalYearEnd)),
            netRevenue: financialStatementRow.netRevenue,
            netIncome: financialStatementRow.netIncome,
          });
        }
      }

      return accumulator;
    }, [])
    .sort((a, b) => {
      return a.fiscalYear - b.fiscalYear;
    });

  return (
    <table
      css={{
        width: "100%",
        borderRadius: 4,
        borderCollapse: "collapse",
        borderSpacing: 0,
        "> thead > tr > th:first-of-type": {
          borderTopLeftRadius: 4,
        },
        "> thead > tr > th:last-of-type": {
          borderTopRightRadius: 4,
        },
        "> tbody > tr:last-of-type > td:first-of-type": {
          borderBottomLeftRadius: 4,
        },
        "> tbody > tr:last-of-type > td:last-of-type": {
          borderBottomRightRadius: 4,
        },
        "> thead > tr > th": {
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
        "& > tbody > tr > td": {
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
        "& > tbody > tr > td:last-of-type": {
          borderRight: "none",
        },
      }}
    >
      <thead>
        <tr>
          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.white,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.blueBayoux,
              borderRight: `solid 1px ${palette.lynch}`,
            }}
          >
            Ano
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.blueBayoux,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            Diretoria Estatutária
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.blueBayoux,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            Conselho de Administração
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.blueBayoux,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.catskillWhite,
              borderRight: `solid 1px ${palette.mystic}`,
            }}
          >
            Conselho Fiscal
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.scienceBlue,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.lilyWhite,
            }}
          >
            Total
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.seaGreen,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/netIncome"
              placement="top"
              content="Receita Líquida - Descontos"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Lucro Líquido
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.luxorGold,
              fontWeight: 600,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            % Lucro
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              padding: "8px 12px",
              color: palette.seaGreen,
              fontWeight: 600,
              lineHeight: "18px",
              fontSize: 14,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/netRevenue"
              placement="top"
              content="Receita Líquida"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Receita
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.luxorGold,
              fontWeight: 600,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            % Receita
          </th>
        </tr>
      </thead>

      <tbody
        css={{
          "& > tr > td": {
            textAlign: "center",
          },
          "& > tr > td:not(:first-of-type)": {
            borderRight: `solid 1px ${palette.mystic}`,
            borderBottom: `solid 1px ${palette.mystic}`,
            borderLeft: `solid 1px ${palette.mystic}`,
          },
          "& > tr:hover": {
            backgroundColor: "rgb(241, 244, 248, 0.5)",
          },
          "& > tr:last-of-type > td": {
            borderBottom: "none",
          },
        }}
      >
        {actualCompensations.map((compensation) => {
          return (
            <tr
              key={compensation.fiscalYear}
              css={{
                "& > td": {
                  padding: "8px 12px",
                },
              }}
            >
              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  backgroundColor: palette.blueBayoux,
                  fontWeight: 600,
                  color: palette.white,
                  lineHeight: "20px",
                  fontSize: 14,
                  borderTop: `solid 1px ${palette.lynch}`,
                  borderBottom: `solid 1px ${palette.lynch}`,
                  borderRight: `solid 1px ${palette.lynch}`,
                }}
              >
                <span>{compensation.fiscalYear}</span>
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.lynch,
                  lineHeight: "20px",
                  fontSize: 14,
                }}
              >
                {numeral(compensation.boardOfExecutiveOfficers).format("0,0").replace(/,/g, ".")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.lynch,
                  lineHeight: "20px",
                  fontSize: 14,
                }}
              >
                {numeral(compensation.boardOfDirectors).format("0,0").replace(/,/g, ".")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.lynch,
                  lineHeight: "20px",
                  fontSize: 14,
                }}
              >
                {numeral(compensation.fiscalCouncil).format("0,0").replace(/,/g, ".")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.scienceBlue,
                  lineHeight: "20px",
                  fontSize: 14,
                }}
              >
                {numeral(compensation.totalCompensation).format("0,0").replace(/,/g, ".")}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: compensation.netIncome >= 0 ? palette.oceanGreen : palette.mojo,
                  lineHeight: "20px",
                  fontSize: 14,
                  border: `solid 1px ${palette.fringyFlower}`,
                  borderRight: "none",
                }}
              >
                {formatFinancialValue(compensation.netIncome)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={
                  compensation.netIncome === 0
                    ? 0
                    : Math.abs((compensation.totalCompensation / compensation.netIncome) * 100)
                }
                comparators={[
                  () => {
                    return compensation.netIncome < 0;
                  },
                  (value) => {
                    return value >= 10;
                  },
                  (value) => {
                    return value < 10 && value > 3;
                  },
                  (value) => {
                    return value <= 3;
                  },
                ]}
                styles={[
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                ]}
              >
                {(ratio, css) => {
                  let formattedRatio = `${(ratio || 0).toFixed(2).replace(".", ",")}%`;

                  if ((ratio || 0) >= 10) {
                    formattedRatio = `${Math.round(ratio || 0)}%`;
                  }

                  if ((ratio || 0) < 0 || compensation.netIncome < 0) {
                    formattedRatio = "P";
                  }

                  return (
                    <td
                      css={{
                        fontFamily: "novel-mono-pro-condensed",
                        fontWeight: 500,
                        color: palette.luxorGold,
                        lineHeight: "20px",
                        fontSize: 14,
                        border: `solid 1px ${palette.chamois}`,
                        borderRight: "none",
                        ...css,
                      }}
                    >
                      {formattedRatio}
                    </td>
                  );
                }}
              </FinancialValueThresholdColumnStyles>

              <td
                css={{
                  color: palette.fruitSalad,
                  fontWeight: 600,
                  fontSize: 14,
                  fontFamily: "novel-mono-pro-condensed",
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(compensation.netRevenue)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={
                  compensation.netRevenue === 0
                    ? 0
                    : Math.abs((compensation.totalCompensation / compensation.netRevenue) * 100)
                }
                comparators={[(value) => value >= 2, (value) => value < 2 && value > 0.5, (value) => value <= 0.5]}
                styles={[
                  () => ({
                    color: palette.mojo,
                    backgroundColor: "#FCF1F1",
                  }),
                  () => ({
                    color: palette.kumera,
                    backgroundColor: palette.butteryWhite,
                    borderBottom: `solid 1px ${palette.chamois}`,
                  }),
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                ]}
              >
                {(ratio, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 500,
                      color: palette.luxorGold,
                      lineHeight: "20px",
                      fontSize: 14,
                      border: `solid 1px ${palette.chamois}`,
                      borderRight: "none",
                      ...css,
                    }}
                  >
                    {(ratio || 0) >= 10 ? Math.round(ratio || 0) : (ratio || 0).toFixed(2).replace(".", ",")}%
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
